import React, { useEffect, useMemo, useState } from "react";
import theme from "../../../styles/theme";
import DropdownList from "../../reusable/Forms/DropdownList";

/* ============================================================================
   BUDGET STRUCTURE BOARD (A1)
   ----------------------------------------------------------------------------
   RÔLE
   - zone principale de lecture de la structure budgétaire
   - affiche une hiérarchie simple en 2 niveaux :
     package -> sous-éléments budgétaires
   - permet une navigation claire et premium, sans logique tableau

   ADAPTATION STRUCTURELLE
   - header local supprimé : désormais piloté par le Banner externe
   - connexion à la Subpage via :
     1. activeFilter
     2. onBannerDataChange
   - compatibilité avec la structure scrollable stabilisée
============================================================================ */

/* ============================================================================
   TOKENS LOCAUX
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  bg: theme?.colors?.background?.surface || "#ffffff",
  panelBg: theme?.colors?.background?.surface || "#ffffff",
  subtleBg: theme?.colors?.background?.hover || "#f7f9fc",

  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.panel || "#cfd8dd",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",

  blueBg: theme?.Generic?.blue?.pastel?.bg || "#E8F2F6",
  greenBg: theme?.Generic?.green?.pastel?.bg || "#E7F3ED",
  yellowBg: theme?.Generic?.yellow?.pastel?.bg || "#F9F3E3",
  greyBg: theme?.Generic?.grey?.pastel?.bg || "#EDF1F4",

  shadowXs: theme?.shadows?.xs || "0 1px 3px rgba(11,39,53,0.08)",
  shadowSm: theme?.shadows?.sm || "0 3px 10px rgba(11,39,53,0.08)",

  radiusMd: theme?.borders?.radius?.md || "3px",
  radiusLg: theme?.borders?.radius?.lg || "6px",
  radiusXl: theme?.borders?.radius?.xl || "8px",
};

/* ============================================================================
   STYLES PARTAGÉS
============================================================================ */

const S = {
  page: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  listPanelControls: {
    display: "flex",
    alignItems: "center",
    gap: 38,
    minWidth: 0,
  },

  listGroupLabel: {
    width: "100%",
    padding: "4px 2px 2px 2px",
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.73rem",
    lineHeight: 1.1,
    color: T.caneva,
    fontWeight: 600,
    boxSizing: "border-box",
  },

  /* ==========================================================================
     WORKSPACE
  ========================================================================== */

  workspace: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.25fr) minmax(320px, 0.95fr)",
    gap: 12,
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  board: {
    width: "100%",
    columnCount: 3,
    columnGap: 10,
    padding: 8,
    border: `1px solid rgba(207,216,221,0.9)`,
    borderRadius: T.radiusMd,
    background: "rgba(11,39,53,0.02)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6)",
    boxSizing: "border-box",
    minHeight: 0,
    height: "100%",
    overflowY: "auto",
    overflowX: "hidden",
  },

  listPanel: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    border: "none",
    borderRadius: T.radiusXl,
    background: "transparent",
    boxShadow: "none",
    boxSizing: "border-box",
    overflow: "hidden",
  },

  listPanelHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "10px 12px 8px 12px",
    borderBottom: `1px solid ${T.border}`,
    boxSizing: "border-box",
    minWidth: 0,
    flex: "0 0 auto",
  },

  listPanelTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.84rem",
    lineHeight: 1.12,
    color: T.text,
    fontWeight: 600,
  },

  listPanelMeta: {
    fontSize: "0.72rem",
    color: T.verySoft,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },

  listPanelBody: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    padding: 8,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  listDropzone: {
    width: "100%",
    minHeight: 180,
    borderRadius: T.radiusLg,
    border: `1px dashed ${T.border}`,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: T.verySoft,
    fontSize: "0.76rem",
    lineHeight: 1.3,
    textAlign: "center",
    padding: 12,
    boxSizing: "border-box",
  },

  /* ==========================================================================
     CARTES PACKAGES
  ========================================================================== */

  boardGroup: {
    width: "100%",
    display: "block",
  },

  parentCard: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: "6px 8px 16px 8px",
    border: `1px solid ${T.borderStrong}`,
    borderRadius: T.radiusXl,
    background: "#ffffff",
    boxSizing: "border-box",
    minHeight: 64,
    position: "relative",
    cursor: "pointer",
    breakInside: "avoid",
    marginBottom: 10,
  },

  parentTopRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "center",
    gap: 8,
  },

  parentTitleWrap: {
    minWidth: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  toggleBtn: {
    width: 11,
    height: 11,
    minWidth: 11,
    borderRadius: 999,
    border: `1px solid ${T.border}`,
    background: "#ffffff",
    color: T.soft,
    fontSize: "0.45rem",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },

  parentTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.75rem",
    lineHeight: 1.1,
    color: T.text,
    fontWeight: 600,
    minWidth: 0,
  },

  parentMeta: {
    display: "none",
  },

  childList: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    paddingLeft: 18,
    boxSizing: "border-box",
  },

  childRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "center",
    gap: 6,
    padding: "5px 6px",
    borderRadius: T.radiusLg,
    background: T.subtleBg,
    border: `1px solid transparent`,
    boxSizing: "border-box",
    minHeight: 28,
  },

  childLeft: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },

  childTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1.1,
    color: T.text,
    fontWeight: 600,
    minWidth: 0,
  },

  childSubtitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.66rem",
    lineHeight: 1.1,
    color: T.verySoft,
    minWidth: 0,
  },

  childRight: {
    display: "none",
  },

  statusPins: {
    position: "absolute",
    right: 8,
    bottom: 6,
    display: "flex",
    alignItems: "center",
    gap: 5,
  },

  statusPin: {
    width: 8,
    height: 8,
    borderRadius: 999,
    flex: "0 0 auto",
  },

  parentCardDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
    pointerEvents: "none",
  },

  /* ==========================================================================
     LIGNES DE LISTE
  ========================================================================== */

  listRow: {
    width: "100%",
    display: "grid",
    gridTemplateColumns: "88px minmax(0, 1fr) 62px 84px auto",
    alignItems: "center",
    gap: 10,
    padding: "5px 10px",
    border: `1px solid ${T.borderStrong}`,
    borderRadius: T.radiusMd,
    background: "#ffffff",
    boxSizing: "border-box",
    cursor: "grab",
  },

  listCellCode: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.73rem",
    lineHeight: 1.1,
    color: T.blueText,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  listCellName: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.74rem",
    lineHeight: 1.15,
    color: T.text,
    fontWeight: 600,
    minWidth: 0,
  },

  listCellUnit: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1,
    color: T.soft,
    fontWeight: 600,
    textAlign: "center",
    whiteSpace: "nowrap",
  },

  listCellZone: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.72rem",
    lineHeight: 1,
    color: T.soft,
    textAlign: "left",
    whiteSpace: "nowrap",
  },

  listCellPins: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 5,
  },

  listRemoveBtn: {
    width: 18,
    height: 18,
    minWidth: 18,
    borderRadius: 999,
    border: `1px solid ${T.border}`,
    background: "#ffffff",
    color: T.soft,
    fontSize: "0.72rem",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },

  emptyState: {
    width: "100%",
    minHeight: 220,
    borderRadius: T.radiusXl,
    border: `1px dashed ${T.border}`,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: T.verySoft,
    fontSize: "0.78rem",
    lineHeight: 1.35,
    textAlign: "center",
    padding: 18,
    boxSizing: "border-box",
  },
};

/* ============================================================================
   MOCK DATA
============================================================================ */

const MOCK_STRUCTURE = [
  {
    id: "pkg-01",
    code: "PK06",
    name: "Réseaux primaires DN1000",
    type: "Package",
    unit: "m",
    zone: "Zone B",
    calculationRule: "QTY",
    budgetReadiness: "Ready",
    children: [
      {
        id: "pkg-01-c1",
        name: "Tuyauterie DN1000",
        subtitle: "Linéaire principal de transport",
      },
      {
        id: "pkg-01-c2",
        name: "Chambres de vannes",
        subtitle: "Ouvrages de sectionnement",
      },
    ],
  },
  {
    id: "pkg-02",
    code: "PK14",
    name: "Bloc traitement – ligne 1",
    type: "Package",
    unit: "LS",
    zone: "Ligne 1",
    calculationRule: "BREAKDOWN",
    budgetReadiness: "To structure",
    children: [
      {
        id: "pkg-02-c1",
        name: "Pompes process",
        subtitle: "Équipements de circulation",
      },
      {
        id: "pkg-02-c2",
        name: "Skids process",
        subtitle: "Assemblages de traitement",
      },
      {
        id: "pkg-02-c3",
        name: "Montage équipements",
        subtitle: "Installation de la ligne",
      },
    ],
  },
  {
    id: "pkg-03",
    code: "PK02",
    name: "Poste électrique principal",
    type: "Package",
    unit: "u",
    zone: "Bloc utilités",
    calculationRule: "BREAKDOWN",
    budgetReadiness: "Upstream review",
    children: [
      {
        id: "pkg-03-c1",
        name: "Armoires électriques",
        subtitle: "Distribution et protection",
      },
      {
        id: "pkg-03-c2",
        name: "Câblage puissance",
        subtitle: "Raccordements principaux",
      },
    ],
  },
  {
    id: "pkg-04",
    code: "PK13",
    name: "Supportage tuyauterie",
    type: "Package",
    unit: "u",
    zone: "Zone C",
    calculationRule: "QTY",
    budgetReadiness: "To structure",
    children: [
      {
        id: "pkg-04-c1",
        name: "Supports métalliques",
        subtitle: "Reprise des charges réseau",
      },
      {
        id: "pkg-04-c2",
        name: "Ancrages et platines",
        subtitle: "Fixations structurelles",
      },
    ],
  },
  {
    id: "pkg-05",
    code: "PK08",
    name: "Réservoir eau traitée",
    type: "Package",
    unit: "m3",
    zone: "Zone A",
    calculationRule: "QTY",
    budgetReadiness: "Ready",
    children: [
      {
        id: "pkg-05-c1",
        name: "Voile béton",
        subtitle: "Enceinte de stockage",
      },
      {
        id: "pkg-05-c2",
        name: "Étanchéité interne",
        subtitle: "Protection de la cuve",
      },
      {
        id: "pkg-05-c3",
        name: "Accessoires hydrauliques",
        subtitle: "Organes de service",
      },
    ],
  },
  {
    id: "pkg-06",
    code: "PK04",
    name: "Station de pompage eau brute",
    type: "Package",
    unit: "LS",
    zone: "Zone prise d’eau",
    calculationRule: "BREAKDOWN",
    budgetReadiness: "To structure",
    children: [
      {
        id: "pkg-06-c1",
        name: "Pompes eau brute",
        subtitle: "Relèvement principal",
      },
      {
        id: "pkg-06-c2",
        name: "Collecteurs aspiration",
        subtitle: "Distribution hydraulique",
      },
      {
        id: "pkg-06-c3",
        name: "Massifs de pose",
        subtitle: "Assises équipements",
      },
    ],
  },
  {
    id: "pkg-07",
    code: "PK09",
    name: "Bâtiment exploitation",
    type: "Package",
    unit: "m2",
    zone: "Bâtiment 1",
    calculationRule: "QTY",
    budgetReadiness: "Ready",
    children: [
      {
        id: "pkg-07-c1",
        name: "Structure bâtiment",
        subtitle: "Clos et couvert",
      },
      {
        id: "pkg-07-c2",
        name: "Second œuvre",
        subtitle: "Aménagements intérieurs",
      },
    ],
  },
  {
    id: "pkg-08",
    code: "PK11",
    name: "Réseau de drainage plateforme",
    type: "Package",
    unit: "m",
    zone: "Plateforme",
    calculationRule: "QTY",
    budgetReadiness: "To structure",
    children: [
      {
        id: "pkg-08-c1",
        name: "Caniveaux béton",
        subtitle: "Collecte eaux de surface",
      },
      {
        id: "pkg-08-c2",
        name: "Regards de visite",
        subtitle: "Accès et entretien",
      },
    ],
  },
  {
    id: "pkg-09",
    code: "PK15",
    name: "Instrumentation ligne 1",
    type: "Package",
    unit: "LS",
    zone: "Ligne 1",
    calculationRule: "LUMP SUM",
    budgetReadiness: "Upstream review",
    children: [
      {
        id: "pkg-09-c1",
        name: "Débitmètres",
        subtitle: "Mesure process en ligne",
      },
      {
        id: "pkg-09-c2",
        name: "Capteurs pression",
        subtitle: "Surveillance hydraulique",
      },
      {
        id: "pkg-09-c3",
        name: "Câbles instrumentation",
        subtitle: "Liaisons terrain",
      },
    ],
  },
  {
    id: "pkg-10",
    code: "PK03",
    name: "Ligne MT et transformateur",
    type: "Package",
    unit: "u",
    zone: "Zone énergie",
    calculationRule: "BREAKDOWN",
    budgetReadiness: "Upstream review",
    children: [
      {
        id: "pkg-10-c1",
        name: "Transformateur MT/BT",
        subtitle: "Abaissement de tension",
      },
      {
        id: "pkg-10-c2",
        name: "Cellules MT",
        subtitle: "Protection et coupure",
      },
    ],
  },
];

const GROUPING_OPTIONS = [
  { key: "zone", label: "Zone" },
  { key: "system", label: "Système" },
  { key: "discipline", label: "Discipline" },
  { key: "packageType", label: "Type de package" },
];

const GROUPING_DROPDOWN_OPTIONS = GROUPING_OPTIONS.map((item) => ({
  value: item.key,
  label: item.label,
}));

/* ============================================================================
   HELPERS
============================================================================ */

const buildMetrics = (rows = [], structuredList = []) => {
  const packageCount = rows.length;
  const itemCount = rows.reduce((acc, row) => acc + (row.children?.length || 0), 0);
  const criticalCount = rows.reduce(
    (acc, row) => acc + (row.budgetReadiness === "Upstream review" ? 1 : 0),
    0
  );

  const structuredCount = structuredList.length;
  const coverageLabel =
    structuredCount >= packageCount && packageCount > 0
      ? "Structured"
      : structuredCount > 0
      ? "Partial"
      : "Initial";

  return {
    packageCount,
    itemCount,
    criticalCount,
    structuredCount,
    coverageLabel,
  };
};

const getCalculationRulePinColor = (rule) => {
  switch (rule) {
    case "QTY":
      return T.green;
    case "BREAKDOWN":
      return "#B8621B";
    case "LUMP SUM":
    case "RATIO":
    case "UNDEFINED":
    default:
      return T.caneva;
  }
};

const getBudgetReadinessPinColor = (readiness) => {
  switch (readiness) {
    case "Ready":
      return T.green;
    case "To structure":
      return "#B8621B";
    case "Upstream review":
    default:
      return T.caneva;
  }
};

const moveItem = (array, fromIndex, toIndex) => {
  const next = [...array];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
};

const getBannerMetrics = (metrics) => [
  {
    label: "Packages",
    value: metrics.packageCount,
    valueColor: T.corelia,
  },
  {
    label: "Structured",
    value: metrics.structuredCount,
  },
  {
    label: "Coverage",
    value: metrics.coverageLabel,
    valueColor:
      metrics.coverageLabel === "Structured"
        ? T.green
        : metrics.coverageLabel === "Partial"
        ? T.orangeText
        : T.soft,
  },
];

const getBannerLegends = () => [
  { label: "Ready", color: T.green },
  { label: "To structure", color: "#B8621B" },
  { label: "Upstream review", color: T.caneva },
];

/* ============================================================================
   COMPOSANTS MÉTIER DU BOARD
============================================================================ */

const ChildItemRow = ({ item }) => (
  <div style={S.childRow}>
    <div style={S.childLeft}>
      <div style={S.childTitle}>{item.name}</div>
      <div style={S.childSubtitle}>{item.subtitle}</div>
    </div>

    <div style={S.childRight} />
  </div>
);

const BudgetStructurePins = ({ group }) => (
  <div style={S.statusPins}>
    <div
      style={{
        ...S.statusPin,
        background: getCalculationRulePinColor(group.calculationRule),
      }}
      title={`Calculation rule: ${group.calculationRule}`}
    />
    <div
      style={{
        ...S.statusPin,
        background: getBudgetReadinessPinColor(group.budgetReadiness),
      }}
      title={`Budget readiness: ${group.budgetReadiness}`}
    />
  </div>
);

const StructuredListRow = ({
  item,
  onDragStart,
  onDragOver,
  onDrop,
  onRemove,
}) => (
  <div
    style={S.listRow}
    draggable
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
  >
    <div style={S.listCellCode}>{item.code}</div>
    <div style={S.listCellName}>{item.name}</div>
    <div style={S.listCellUnit}>{item.unit}</div>
    <div style={S.listCellZone}>{item.zone}</div>
    <div style={S.listCellPins}>
      <div
        style={{
          ...S.statusPin,
          background: getCalculationRulePinColor(item.calculationRule),
        }}
      />
      <div
        style={{
          ...S.statusPin,
          background: getBudgetReadinessPinColor(item.budgetReadiness),
        }}
      />
      <button
        type="button"
        style={S.listRemoveBtn}
        onClick={(e) => {
          e.stopPropagation();
          onRemove?.(item.id);
        }}
        title="Retirer de la liste"
        aria-label="Retirer de la liste"
      >
        ×
      </button>
    </div>
  </div>
);

/* --------------------------------------------------------------------------
   Préparation connexion A2
   - clic sur la carte = émission de l’objet sélectionné
   - drag de la carte = ajout dans la liste structurante
-------------------------------------------------------------------------- */
const BudgetStructureGroup = ({
  group,
  defaultOpen = true,
  onSelectGroup,
  onDragPackageStart,
  isStructured = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={S.boardGroup}>
      <div
        style={{
          ...S.parentCard,
          ...(isStructured ? S.parentCardDisabled : {}),
        }}
        draggable={!isStructured}
        onDragStart={(e) => {
          if (!isStructured) onDragPackageStart?.(e, group);
        }}
        onClick={() => {
          if (!isStructured) onSelectGroup?.(group);
        }}
        role="button"
        tabIndex={0}
      >
        <div style={S.parentTopRow}>
          <div style={S.parentTitleWrap}>
            <button
              type="button"
              style={S.toggleBtn}
              onClick={(e) => {
                e.stopPropagation();
                setOpen((prev) => !prev);
              }}
              aria-label={open ? "Collapse group" : "Expand group"}
            >
              {open ? "−" : "+"}
            </button>

            <div style={S.parentTitle}>
              {group.code} {group.name}
            </div>
          </div>

          <div style={S.parentMeta} />
        </div>

        {open && (
          <div style={S.childList}>
            {group.children?.map((child) => (
              <ChildItemRow key={child.id} item={child} />
            ))}
          </div>
        )}

        <BudgetStructurePins group={group} />
      </div>
    </div>
  );
};

/* ============================================================================
   MAIN COMPONENT
   ----------------------------------------------------------------------------
   Props de connexion attendues depuis la sous-page :
   - activeFilter
   - onBannerDataChange
   - onSelectPackage
============================================================================ */

const BudgetStructureBoard = ({
  rows = MOCK_STRUCTURE,
  onSelectPackage,
  activeFilter = "Structure",
  onBannerDataChange,
}) => {
  const [structuredList, setStructuredList] = useState([]);
  const [groupBy, setGroupBy] = useState("zone");

  const metrics = useMemo(
    () => buildMetrics(rows, structuredList),
    [rows, structuredList]
  );

  /* ==========================================================================
     REMONTÉE DES DONNÉES VERS LE BANNER
  ========================================================================== */
  useEffect(() => {
    if (typeof onBannerDataChange !== "function") return;

    onBannerDataChange({
      metrics: getBannerMetrics(metrics),
      filterOptions: ["Structure", "Coverage", "Readiness"],
      legends: getBannerLegends(),
      showMetrics: true,
      showFilters: true,
      showLegends: true,
    });
  }, [metrics, onBannerDataChange]);

  /* ==========================================================================
     GROUPEMENT DE LA LISTE STRUCTURÉE
  ========================================================================== */
  const groupPackages = (list, key) => {
    return list.reduce((acc, item) => {
      const groupValue = item[key] || "Non défini";

      if (!acc[groupValue]) acc[groupValue] = [];
      acc[groupValue].push(item);

      return acc;
    }, {});
  };

  const groupedStructuredList = groupPackages(structuredList, groupBy);

  /* ==========================================================================
     FILTRAGE PILOTÉ PAR LE BANNER
     --------------------------------------------------------------------------
     - Structure : vue complète
     - Coverage  : focalisation sur la liste structurée
     - Readiness : focalisation sur les packages source
  ========================================================================== */
  const showSourceBoard =
    activeFilter === "Structure" || activeFilter === "Readiness";

  const showStructuredPanel =
    activeFilter === "Structure" || activeFilter === "Coverage";

  /* ==========================================================================
     DRAG & DROP
  ========================================================================== */
  const handlePackageDragStart = (event, group) => {
    event.dataTransfer.setData(
      "application/corelia-package",
      JSON.stringify(group)
    );
    event.dataTransfer.effectAllowed = "copyMove";
  };

  const handleListDrop = (event) => {
    event.preventDefault();

    const packageData = event.dataTransfer.getData("application/corelia-package");
    const listData = event.dataTransfer.getData("application/corelia-list-row");

    if (packageData) {
      const droppedPackage = JSON.parse(packageData);

      setStructuredList((prev) => {
        if (prev.some((item) => item.id === droppedPackage.id)) return prev;
        return [...prev, droppedPackage];
      });

      return;
    }

    if (listData && structuredList.length > 1) {
      const draggedId = listData;
      const fromIndex = structuredList.findIndex((item) => item.id === draggedId);
      if (fromIndex === -1) return;
      setStructuredList((prev) => moveItem(prev, fromIndex, prev.length - 1));
    }
  };

  const handleListRowDragStart = (event, item) => {
    event.dataTransfer.setData("application/corelia-list-row", item.id);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleListRowDrop = (event, targetItem) => {
    event.preventDefault();

    const draggedId = event.dataTransfer.getData("application/corelia-list-row");
    if (!draggedId || draggedId === targetItem.id) return;

    setStructuredList((prev) => {
      const fromIndex = prev.findIndex((item) => item.id === draggedId);
      const toIndex = prev.findIndex((item) => item.id === targetItem.id);
      if (fromIndex === -1 || toIndex === -1) return prev;
      return moveItem(prev, fromIndex, toIndex);
    });
  };

  const handleListDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleRemoveFromStructuredList = (itemId) => {
    setStructuredList((prev) => prev.filter((item) => item.id !== itemId));
  };

  if (!rows?.length) {
    return (
      <div style={S.page}>
        <div style={S.emptyState}>Aucune structure budgétaire disponible.</div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.workspace}>
        {showSourceBoard ? (
          <div style={S.board}>
            {rows.map((group) => (
              <BudgetStructureGroup
                key={group.id}
                group={group}
                onSelectGroup={onSelectPackage}
                onDragPackageStart={handlePackageDragStart}
                isStructured={structuredList.some((item) => item.id === group.id)}
              />
            ))}
          </div>
        ) : (
          <div style={S.emptyState}>
            La vue source est masquée par le filtre actif.
          </div>
        )}

        {showStructuredPanel ? (
          <div style={S.listPanel}>
            <div style={S.listPanelHeader}>
              <div style={S.listPanelTitle}>Structured list</div>

              <div style={S.listPanelControls}>
                <DropdownList
                  label=""
                  labelPosition="top"
                  options={GROUPING_DROPDOWN_OPTIONS}
                  value={groupBy}
                  onChange={setGroupBy}
                  placeholder="Structuration"
                  width="160px"
                  inputMinHeight={28}
                  style={{ margin: 0 }}
                  wrapperMarginTop={0}
                  wrapperMarginLeft={0}
                />

                <div style={S.listPanelMeta}>
                  {structuredList.length} package(s)
                </div>
              </div>
            </div>

            <div
              style={S.listPanelBody}
              onDragOver={handleListDragOver}
              onDrop={handleListDrop}
            >
              {!structuredList.length ? (
                <div style={S.listDropzone}>
                  Glisser-déposer les packages ici pour composer la liste
                  structurante.
                </div>
              ) : (
                Object.entries(groupedStructuredList).map(([groupLabel, items]) => (
                  <React.Fragment key={groupLabel}>
                    <div style={S.listGroupLabel}>{groupLabel}</div>

                    {items.map((item) => (
                      <StructuredListRow
                        key={item.id}
                        item={item}
                        onDragStart={(e) => handleListRowDragStart(e, item)}
                        onDragOver={handleListDragOver}
                        onDrop={(e) => handleListRowDrop(e, item)}
                        onRemove={handleRemoveFromStructuredList}
                      />
                    ))}
                  </React.Fragment>
                ))
              )}
            </div>
          </div>
        ) : (
          <div style={S.emptyState}>
            La vue de couverture est masquée par le filtre actif.
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetStructureBoard;